import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import Alert from "@/components/Core/Alert";
import CertificationSelectPopover, {
	CertificationSelectOption,
} from "@/components/Shared/CertificationSelectPopover";
import TagSelectPopover, { TagSelectOption } from "@/components/Shared/TagSelectPopover";
import CertificationChip from "@/components/Shared/TagChip/CertificationChip";
import TagChip from "@/components/Shared/TagChip/TagChip";
import Competence from "@/models/Competence";
import { JobRequirements } from "@/models/Job";
import Organisation from "@/models/Organisation";
import { certificationTypeName, consultantTagName } from "@/utils/credential.utils";

export const EMPTY_REQUIREMENTS: JobRequirements = { certifications: [], tags: [] };

/**
 * Resolve the requirement set for a job from the org's
 * `competenceRequirements` rules (client-side mirror of the backend
 * derivation): a rule matches when ALL of its present scope conditions hold —
 * its branch equals the competence branch (service/skill unset or equal), and
 * its customerTag is carried by the job's customer. Rules with no scope
 * condition at all are ignored. The result is the union of all matching
 * rules' `requires`, split into certifications and tags.
 */
export function deriveRequirements(
	organisation: Organisation | undefined,
	competence: Partial<Competence> | undefined,
	customerTags: { key: string }[] = []
): JobRequirements {
	const certifications = new Set<string>();
	const tags = new Set<string>();

	const customerTagKeys = new Set(customerTags.map((tag) => tag.key));

	if (organisation?.competenceRequirements) {
		for (const rule of organisation.competenceRequirements) {
			if (!rule.branch && !rule.customerTag) continue;
			if (rule.branch) {
				if (rule.branch !== competence?.branch) continue;
				if (rule.service && rule.service !== competence?.service) continue;
				if (rule.skill && !competence?.skills?.includes(rule.skill)) continue;
			}
			if (rule.customerTag && !customerTagKeys.has(rule.customerTag)) continue;
			for (const req of rule.requires ?? []) {
				if (req.kind === "certification") certifications.add(req.key);
				else tags.add(req.key);
			}
		}
	}

	return {
		certifications: [...certifications].map((typeKey) => ({ typeKey })),
		tags: [...tags].map((key) => ({ key })),
	};
}

/** Join names as ”A”, ”B” och ”C”. */
function joinQuotedNames(names: string[]): string {
	const quoted = names.map((name) => `”${name}”`);
	if (quoted.length <= 1) return quoted.join("");
	return `${quoted.slice(0, -1).join(", ")} och ${quoted[quoted.length - 1]}`;
}

/** Order-insensitive identity of a requirement set, for change detection. */
function requirementsKey(requirements: JobRequirements): string {
	return [
		...requirements.certifications.map((c) => `c:${c.typeKey}`).sort(),
		...requirements.tags.map((t) => `t:${t.key}`).sort(),
	].join("|");
}

/**
 * The requirement set after the rule-derived suggestions change (competence
 * changed): the new derived set, plus the admin's manual additions (= entries
 * in `current` that were not part of the previous derived set). Entries the
 * admin removed from the previous derived set do not carry over — the new
 * competence's rules win, mirroring the backend's re-derivation.
 */
export function mergeDerivedRequirements(
	prevDerived: JobRequirements,
	newDerived: JobRequirements,
	current: JobRequirements
): JobRequirements {
	const prevDerivedCerts = new Set(prevDerived.certifications.map((c) => c.typeKey));
	const prevDerivedTags = new Set(prevDerived.tags.map((t) => t.key));

	const certifications = [...newDerived.certifications];
	for (const cert of current.certifications) {
		if (prevDerivedCerts.has(cert.typeKey)) continue;
		if (!certifications.some((c) => c.typeKey === cert.typeKey)) certifications.push(cert);
	}

	const tags = [...newDerived.tags];
	for (const tag of current.tags) {
		if (prevDerivedTags.has(tag.key)) continue;
		if (!tags.some((t) => t.key === tag.key)) tags.push(tag);
	}

	return { certifications, tags };
}

/**
 * Editable per-job requirement state (the job's MATCHING GATE), kept in sync
 * with the org's matchningsregler: when the competence or the customer's tags
 * change, the rule-derived suggestions are re-derived and unioned with the
 * admin's manual additions. Manual edits otherwise stick.
 *
 * `customerTags` must be `undefined` while the customer is still loading —
 * the baseline is deferred until the tags are known, otherwise customer-tag
 * rules would look newly-added when the customer arrives and entries the
 * admin removed from the derived set would reappear. Pass `[]` when there is
 * no customer to load.
 *
 * `resetRequirements(next)` replaces the state (e.g. from a loaded/copied
 * job's snapshot) and re-baselines the derived set for the current
 * competence/customer so existing entries are not treated as manual additions
 * of a later merge.
 */
export function useEditableJobRequirements(
	organisation: Organisation | undefined,
	competence: Partial<Competence> | undefined,
	customerTags: { key: string }[] | undefined
) {
	const [requirements, setRequirements] = useState<JobRequirements>(EMPTY_REQUIREMENTS);

	const lastDerivedRef = useRef<JobRequirements>(EMPTY_REQUIREMENTS);
	const initializedRef = useRef(false);

	// Latest values for the stable resetRequirements callback
	const organisationRef = useRef(organisation);
	organisationRef.current = organisation;
	const competenceRef = useRef(competence);
	competenceRef.current = competence;
	const customerTagsRef = useRef(customerTags);
	customerTagsRef.current = customerTags;

	const competenceKey = `${competence?.branch ?? ""}|${competence?.service ?? ""}|${(
		competence?.skills ?? []
	).join(",")}`;
	const customerTagsKey = customerTags
		? [...customerTags.map((tag) => tag.key)].sort().join(",")
		: undefined;

	useEffect(() => {
		if (!organisation || !customerTags) return;

		const newDerived = deriveRequirements(organisation, competence, customerTags);

		// First run with the org available: baseline only — the current state
		// (empty form, or a loaded job's snapshot) must not be re-merged, or
		// entries the admin removed from the derived set would reappear.
		if (!initializedRef.current) {
			initializedRef.current = true;
			lastDerivedRef.current = newDerived;
			return;
		}

		if (requirementsKey(newDerived) === requirementsKey(lastDerivedRef.current)) return;

		const prevDerived = lastDerivedRef.current;
		lastDerivedRef.current = newDerived;
		setRequirements((current) => mergeDerivedRequirements(prevDerived, newDerived, current));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organisation, competenceKey, customerTagsKey]);

	const resetRequirements = useCallback((next?: JobRequirements) => {
		setRequirements({
			certifications: [...(next?.certifications ?? [])],
			tags: [...(next?.tags ?? [])],
		});

		// Only baseline when the derived set can be fully resolved — if the
		// customer is still loading, the effect above baselines once it arrives.
		if (organisationRef.current && customerTagsRef.current) {
			lastDerivedRef.current = deriveRequirements(
				organisationRef.current,
				competenceRef.current,
				customerTagsRef.current
			);
			initializedRef.current = true;
		} else {
			initializedRef.current = false;
		}
	}, []);

	return { requirements, setRequirements, resetRequirements };
}

interface JobRequirementsEditorProps {
	organisation?: Organisation;
	/**
	 * The job's competence — used to re-derive the rule-based requirement set so
	 * derived entries can be marked (✨) and removals of them warned about.
	 */
	competence?: Partial<Competence>;
	/**
	 * The job's customer's tags — customer-tag scoped matchningsregler resolve
	 * against these in the same re-derivation.
	 */
	customerTags?: { key: string }[];
	/** The job's current requirement set (rule-derived suggestions + manual edits). */
	value: JobRequirements;
	onChange: (requirements: JobRequirements) => void;
	disabled?: boolean;
	className?: string;
}

/**
 * Editor for the job's MATCHING GATE (`job.requirements`): consultants must
 * validly hold ALL listed certifications/tags to see the job in their feed,
 * self-apply or be auto-booked. Suggestions come from the org's
 * matchningsregler (Inställningar → Matchningsregler) for the selected
 * competence — they arrive pre-selected in `value` and show as checked in the
 * pickers — and are freely editable per job, additions and removals alike.
 *
 * Adding rides the shared dashed-AddButton pickers ({@link TagSelectPopover} +
 * {@link CertificationSelectPopover}). The selected requirements render with
 * the platform-standard chips — {@link CertificationChip} (white award pill)
 * and {@link TagChip} (Trello-colored label) — with an inline ✕ to remove.
 *
 * Rule-derived entries carry a ✨ marker so the admin can tell them from
 * manual additions. Removing one is allowed but flags a persistent,
 * non-blocking warning that the job then deviates from the org's rules.
 */
const JobRequirementsEditor: React.FC<JobRequirementsEditorProps> = ({
	organisation,
	competence,
	customerTags,
	value,
	onChange,
	disabled,
	className,
}) => {
	const certificationTypes = organisation?.certificationTypes ?? [];
	const consultantTagTypes = organisation?.consultantTagTypes ?? [];

	const selectedCertificationKeys = value.certifications.map((c) => c.typeKey);
	const selectedTagKeys = value.tags.map((t) => t.key);

	// What the org's matchningsregler prescribe for the current competence and
	// customer — used only for marking/warning; the actual gate is `value`.
	const derived = deriveRequirements(organisation, competence, customerTags);
	const derivedCertificationKeys = new Set(derived.certifications.map((c) => c.typeKey));
	const derivedTagKeys = new Set(derived.tags.map((t) => t.key));

	const removedDerivedCertificationNames = derived.certifications
		.filter((c) => !selectedCertificationKeys.includes(c.typeKey))
		.map((c) => certificationTypeName(organisation, c.typeKey));
	const removedDerivedTagNames = derived.tags
		.filter((tag) => !selectedTagKeys.includes(tag.key))
		.map((tag) => consultantTagName(organisation, tag.key));
	const removedDerivedCount =
		removedDerivedCertificationNames.length + removedDerivedTagNames.length;

	// "Certifikatet ”A”" / "certifikaten ”A” och ”B”" / "taggen ”C”" …,
	// combined with " och " when both kinds are missing.
	const removedDerivedSubject = [
		removedDerivedCertificationNames.length > 0
			? `${removedDerivedCertificationNames.length > 1 ? "certifikaten" : "certifikatet"} ${joinQuotedNames(removedDerivedCertificationNames)}`
			: null,
		removedDerivedTagNames.length > 0
			? `${removedDerivedTagNames.length > 1 ? "taggarna" : "taggen"} ${joinQuotedNames(removedDerivedTagNames)}`
			: null,
	]
		.filter(Boolean)
		.join(" och ");

	const removedDerivedNoun = [
		removedDerivedCertificationNames.length > 0 ? "certifikat" : null,
		removedDerivedTagNames.length > 1
			? "taggar"
			: removedDerivedTagNames.length === 1
				? "tagg"
				: null,
	]
		.filter(Boolean)
		.join(" och ");

	const hasSelectedDerived =
		selectedCertificationKeys.some((key) => derivedCertificationKeys.has(key)) ||
		selectedTagKeys.some((key) => derivedTagKeys.has(key));

	const derivedTitle = "Tillagd automatiskt enligt organisationens matchningsregler";
	const derivedIndicator = (
		<Sparkles size={12} className="shrink-0 opacity-70" aria-label={derivedTitle} />
	);

	const certificationOptions: CertificationSelectOption[] = certificationTypes.map(
		(certificationType) => ({
			key: certificationType.key,
			name: certificationType.name.sv,
		})
	);
	const tagOptions: TagSelectOption[] = consultantTagTypes.map((tagType) => ({
		key: tagType.key,
		name: tagType.name.sv,
		color: tagType.color,
	}));

	const addCertification = (typeKey: string) => {
		if (!selectedCertificationKeys.includes(typeKey))
			onChange({ ...value, certifications: [...value.certifications, { typeKey }] });
	};

	const removeCertification = (typeKey: string) =>
		onChange({
			...value,
			certifications: value.certifications.filter((c) => c.typeKey !== typeKey),
		});

	const addTag = (key: string) => {
		if (!selectedTagKeys.includes(key)) onChange({ ...value, tags: [...value.tags, { key }] });
	};

	const removeTag = (key: string) =>
		onChange({ ...value, tags: value.tags.filter((t) => t.key !== key) });

	const count = value.certifications.length + value.tags.length;

	if (count === 0 && certificationOptions.length === 0 && tagOptions.length === 0) return null;

	return (
		<div className={cn("flex flex-col gap-3", className)}>
			{count > 0 && (
				<div className="flex flex-wrap items-center gap-1.5">
					{value.certifications.map((req) => {
						const name = certificationTypeName(organisation, req.typeKey);
						const isDerived = derivedCertificationKeys.has(req.typeKey);
						return (
							<CertificationChip
								key={`certification:${req.typeKey}`}
								name={name}
								title={isDerived ? `${name} — ${derivedTitle}` : name}
								indicator={isDerived ? "*" : undefined}
								onRemove={() => removeCertification(req.typeKey)}
								removeDisabled={disabled}
							/>
						);
					})}
					{value.tags.map((req) => {
						const name = consultantTagName(organisation, req.key);
						const isDerived = derivedTagKeys.has(req.key);
						return (
							<TagChip
								key={`tag:${req.key}`}
								name={name}
								title={isDerived ? `${name} — ${derivedTitle}` : name}
								indicator={isDerived ? derivedIndicator : undefined}
								color={consultantTagTypes.find((tagType) => tagType.key === req.key)?.color}
								showIcon
								onRemove={() => removeTag(req.key)}
								removeDisabled={disabled}
							/>
						);
					})}
				</div>
			)}

			{removedDerivedCount > 0 && (
				<Alert
					variant="warning"
					skipTranslation
					title="Krav enligt matchningsregler saknas"
					className="p-3 max-w-[600px]"
				>
					<div className="space-y-1">
						<p className="m-0">
							{removedDerivedSubject.charAt(0).toUpperCase() + removedDerivedSubject.slice(1)}{" "}
							krävs enligt organisationens matchningsregler (se Bokning & matchning under
							Inställningar).
						</p>
						<p className="m-0">
							Kompetensprofilen för detta uppdrag saknar ovanstående {removedDerivedNoun} (som
							dock kan läggas till manuellt).
						</p>
						<p className="m-0">
							Uppdraget kan sparas och publiceras ändå, men matchningen ställer då inte{" "}
							{removedDerivedCount > 1 ? "dessa krav" : "detta krav"}.
						</p>
					</div>
				</Alert>
			)}

			{(certificationOptions.length > 0 || tagOptions.length > 0) && (
				<div className="flex flex-wrap items-center gap-2">
					{certificationOptions.length > 0 && (
						<CertificationSelectPopover
							options={certificationOptions}
							selectedKeys={selectedCertificationKeys}
							onSelect={addCertification}
							onDeselect={removeCertification}
							disabled={disabled}
						/>
					)}
					{tagOptions.length > 0 && (
						<TagSelectPopover
							options={tagOptions}
							selectedKeys={selectedTagKeys}
							onSelect={addTag}
							onDeselect={removeTag}
							heading="Konsulttaggar"
							disabled={disabled}
						/>
					)}
				</div>
			)}

			{hasSelectedDerived && (
				<p className="m-0 flex items-center gap-1 text-xs text-muted-foreground italic">
					{/* <Sparkles size={11} className="shrink-0" /> */}* Krav enligt matchningsregler.
				</p>
			)}
		</div>
	);
};

export default JobRequirementsEditor;
